/**
 * Chapter-level "Storyboards & videos" dialog.
 *
 * After an admin uses AI to split a chapter PDF into N lessons, they hit
 * the "Storyboards & videos" button on the chapter row to:
 *   1. Fan out AI storyboard generation across every lesson in the chapter
 *      ("Generate all storyboards") — runs one HTTP call per lesson with
 *      a small concurrency cap to keep OpenAI happy.
 *   2. Step through each lesson's storyboard with ← / → keyboard or
 *      arrow buttons, editing slide headings, bullets, and narration
 *      inline.
 *   3. Render an MP4 for a single lesson, or "Render all" — every render
 *      registers with the global `AiJobsContext` tray so the admin can
 *      close this dialog and the renders keep going.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAiJobs } from "@/lib/ai-jobs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Wand2, Sparkles, Film, ChevronLeft, ChevronRight, Loader2,
    CheckCircle2, AlertCircle, Save, Play, Layers, Clock,
} from "lucide-react";
import { toast } from "sonner";

// Per-lesson AI calls run with this concurrency so we don't slam OpenAI
// with 10 simultaneous calls (some accounts cap at 3-5 concurrent).
const AI_CONCURRENCY = 3;

/**
 * Tiny pool helper: runs `fn(item)` for every item in `items` with at most
 * `limit` calls in flight at once. Resolves once all are done.
 */
async function runPool(items, limit, fn) {
    const results = new Array(items.length);
    let cursor = 0;
    const worker = async () => {
        while (cursor < items.length) {
            const idx = cursor++;
            try {
                results[idx] = await fn(items[idx], idx);
            } catch (e) {
                results[idx] = { error: e };
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
}

function ensureSlideShape(slide, i) {
    return {
        number: slide?.number ?? i + 1,
        heading: slide?.heading ?? "",
        bullets: Array.isArray(slide?.bullets) ? slide.bullets : [],
        narration: slide?.narration ?? "",
    };
}

function normalizeStoryboard(sb) {
    if (!sb) return null;
    return {
        title: sb.title ?? "",
        estimated_duration_sec: sb.estimated_duration_sec,
        slides: (sb.slides || []).map(ensureSlideShape),
    };
}

/**
 * Match the same formula the individual-lesson `VideoStoryboardDialog`
 * uses so the chapter-level overview and the per-lesson preview agree on
 * the same "~N min" estimate. The backend storyboard generator usually
 * includes `estimated_duration_sec`; if it's missing we fall back to 35 s
 * per slide which matches the TTS pacing we use at render time.
 */
function boardDurationMinutes(board) {
    if (!board || !(board.slides || []).length) return 0;
    const secs = board.estimated_duration_sec || board.slides.length * 35;
    return Math.max(1, Math.round(secs / 60));
}

export default function RenderAllVideosDialog({
    open, onOpenChange, chapterId, chapterTitle, lessons, onRefresh,
}) {
    const { trackJob } = useAiJobs();

    // Local copy of each lesson's storyboard, keyed by lesson.id, so the
    // user's in-progress edits don't get clobbered by background refreshes.
    const [boards, setBoards] = useState({});       // { [lessonId]: storyboard }
    const [statuses, setStatuses] = useState({});    // { [lessonId]: "idle" | "generating" | "saving" | "rendering" | "error" }
    const [errors, setErrors] = useState({});        // { [lessonId]: msg }
    const [active, setActive] = useState(0);
    const dirtyRef = useRef({});                     // lessons with unsaved edits

    // Hydrate state whenever the dialog opens or a new lesson appears in
    // the chapter (e.g. AI split added one). We deliberately do NOT replace
    // boards we've already seen — otherwise a background refresh after a
    // render would wipe a user's mid-edit changes. Editable status, errors,
    // dirty flags, and active index also survive refreshes.
    useEffect(() => {
        if (!open) return;
        setBoards((prev) => {
            const next = { ...prev };
            for (const l of lessons || []) {
                if (!(l.id in next)) {
                    next[l.id] = normalizeStoryboard(l.video_storyboard);
                } else if (!next[l.id] && l.video_storyboard) {
                    // Lesson previously had no board; pick up the new one.
                    next[l.id] = normalizeStoryboard(l.video_storyboard);
                }
            }
            return next;
        });
    }, [open, lessons]);

    // Reset transient state only when the dialog is closed → reopened, not
    // on every lesson-prop change.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setStatuses({});
            setErrors({});
            dirtyRef.current = {};
            setActive(0);
        }
        wasOpenRef.current = open;
    }, [open]);

    const currentLesson = lessons?.[active] || null;
    const currentBoard = currentLesson ? boards[currentLesson.id] : null;

    /* ------------------ keyboard nav (← / →) ------------------ */
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            // Don't hijack arrow keys while the user is typing in a field.
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea") return;
            if (e.key === "ArrowLeft") {
                setActive((i) => Math.max(0, i - 1));
            } else if (e.key === "ArrowRight") {
                setActive((i) => Math.min((lessons?.length || 1) - 1, i + 1));
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, lessons?.length]);

    /* ------------------ storyboard helpers -------------------- */

    const setStatus = useCallback((lessonId, s) => {
        setStatuses((prev) => ({ ...prev, [lessonId]: s }));
    }, []);
    const setError = useCallback((lessonId, msg) => {
        setErrors((prev) => ({ ...prev, [lessonId]: msg }));
    }, []);
    const clearError = useCallback((lessonId) => {
        setErrors((prev) => {
            const n = { ...prev }; delete n[lessonId]; return n;
        });
    }, []);

    const updateBoard = (lessonId, mut) => {
        setBoards((prev) => {
            const cur = prev[lessonId];
            if (!cur) return prev;
            return { ...prev, [lessonId]: mut(cur) };
        });
        dirtyRef.current[lessonId] = true;
    };

    const generateOne = async (lesson) => {
        setStatus(lesson.id, "generating");
        clearError(lesson.id);
        try {
            const { data } = await api.post(
                `/lessons/${lesson.id}/storyboard/generate`,
                {},
                { timeout: 120 * 1000 },
            );
            const sb = normalizeStoryboard(data?.storyboard);
            setBoards((prev) => ({ ...prev, [lesson.id]: sb }));
            // Server already saved; drop the dirty flag.
            delete dirtyRef.current[lesson.id];
            setStatus(lesson.id, "idle");
            return sb;
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Storyboard generation failed.";
            setError(lesson.id, msg);
            setStatus(lesson.id, "error");
            return null;
        }
    };

    const generateAll = async () => {
        const toGen = (lessons || []).filter((l) => !boards[l.id]);
        if (!toGen.length) {
            toast.info("Every lesson already has a storyboard.");
            return;
        }
        toast.message(`Generating ${toGen.length} storyboard${toGen.length === 1 ? "" : "s"}…`);
        await runPool(toGen, AI_CONCURRENCY, generateOne);
        toast.success("Storyboards drafted. Review & edit before rendering.");
    };

    const saveOne = async (lesson) => {
        const sb = boards[lesson.id];
        if (!sb) return false;
        setStatus(lesson.id, "saving");
        try {
            await api.patch(`/lessons/${lesson.id}`, { video_storyboard: sb });
            delete dirtyRef.current[lesson.id];
            setStatus(lesson.id, "idle");
            return true;
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not save storyboard.";
            setError(lesson.id, msg);
            setStatus(lesson.id, "error");
            return false;
        }
    };

    const renderOne = async (lesson) => {
        // Auto-save any pending edits so the render uses the latest copy.
        if (dirtyRef.current[lesson.id]) {
            const ok = await saveOne(lesson);
            if (!ok) return;
        }
        setStatus(lesson.id, "rendering");
        try {
            const { data } = await api.post(`/lessons/${lesson.id}/render-video`, {});
            trackJob({
                kind: "video",
                jobId: data.job_id,
                lessonId: lesson.id,
                lessonTitle: data.lesson_title || lesson.title || "Lesson",
                chapterTitle: data.chapter_title || chapterTitle || "Chapter",
                total: data.total,
                onDone: () => {
                    try {
                        window.dispatchEvent(new CustomEvent("cco:lesson-video-rendered", {
                            detail: { lessonId: lesson.id },
                        }));
                    } catch { /* no-op */ }
                    onRefresh?.();
                },
            });
            setStatus(lesson.id, "idle");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not start render.";
            setError(lesson.id, msg);
            setStatus(lesson.id, "error");
        }
    };

    const renderAll = async () => {
        const eligible = (lessons || []).filter(
            (l) => (boards[l.id]?.slides || []).length > 0,
        );
        if (!eligible.length) {
            toast.error("Generate storyboards first.");
            return;
        }
        toast.success(`Queueing ${eligible.length} video render${eligible.length === 1 ? "" : "s"}. Track progress in the bottom-right tray — you can close this dialog.`);
        // Renders are themselves background-jobbed; we just need to kick
        // them. Run with the same small concurrency so we don't spam the
        // kick endpoint either.
        await runPool(eligible, AI_CONCURRENCY, renderOne);
    };

    /* ------------------ summary counts (header) --------------- */

    const summary = useMemo(() => {
        const total = lessons?.length || 0;
        let withBoard = 0;
        let renderingCount = 0;
        let totalSeconds = 0;
        for (const l of lessons || []) {
            const board = boards[l.id];
            const slideCount = (board?.slides || []).length;
            if (slideCount > 0) {
                withBoard += 1;
                totalSeconds += board.estimated_duration_sec || slideCount * 35;
            }
            if (statuses[l.id] === "rendering") renderingCount += 1;
        }
        const totalMinutes = totalSeconds > 0 ? Math.max(1, Math.round(totalSeconds / 60)) : 0;
        return { total, withBoard, missing: total - withBoard, renderingCount, totalMinutes };
    }, [lessons, boards, statuses]);

    /* ------------------ render -------------------------------- */

    if (!currentLesson) return null;
    const status = statuses[currentLesson.id] || "idle";
    const isGenerating = status === "generating";
    const isSaving = status === "saving";
    const isRendering = status === "rendering";
    const lessonError = errors[currentLesson.id];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-5xl w-[min(1100px,calc(100vw-2rem))] max-h-[90vh] flex flex-col p-0 gap-0"
                data-testid="render-all-videos-dialog"
            >
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-border space-y-1.5">
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <Film className="h-5 w-5 text-primary" />
                        Storyboards &amp; videos — {chapterTitle || "Chapter"}
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Step through each lesson with ← / →, edit slide headings, bullets,
                        and narration, then render an MP4. Renders run in the background — you
                        can close this dialog and progress continues in the bottom-right tray.
                    </DialogDescription>

                    <div className="flex flex-wrap items-center gap-2 pt-2">
                        <div className="text-xs text-muted-foreground mr-auto">
                            <strong className="text-foreground">{summary.withBoard}</strong> of {summary.total} lesson{summary.total === 1 ? "" : "s"} have a storyboard
                            {summary.totalMinutes > 0 && (
                                <> · <strong className="text-foreground">~{summary.totalMinutes} min</strong> total runtime</>
                            )}
                            {summary.renderingCount > 0 && (
                                <> · <strong className="text-primary">{summary.renderingCount}</strong> rendering</>
                            )}
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={generateAll}
                            disabled={summary.missing === 0}
                            data-testid="rav-generate-all"
                            className="gap-1.5"
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Generate all storyboards
                            {summary.missing > 0 && (
                                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-accent/30 text-[10px] px-1.5">
                                    {summary.missing}
                                </span>
                            )}
                        </Button>
                        <Button
                            size="sm"
                            onClick={renderAll}
                            disabled={summary.withBoard === 0}
                            data-testid="rav-render-all"
                            className="gap-1.5 bg-primary hover:bg-primary/90"
                        >
                            <Play className="h-3.5 w-3.5" />
                            Render all videos
                        </Button>
                    </div>
                </DialogHeader>

                {/* Body: lesson sidebar + storyboard editor */}
                <div className="flex flex-1 min-h-0 overflow-hidden">
                    {/* Sidebar — lesson list */}
                    <aside
                        className="w-[260px] shrink-0 border-r border-border overflow-y-auto bg-muted/30"
                        data-testid="rav-lesson-list"
                    >
                        {lessons.map((l, i) => {
                            const board = boards[l.id];
                            const slideCount = (board?.slides || []).length;
                            const has = slideCount > 0;
                            const mins = boardDurationMinutes(board);
                            const isActive = i === active;
                            const stLabel = statuses[l.id];
                            return (
                                <button
                                    key={l.id}
                                    type="button"
                                    onClick={() => setActive(i)}
                                    className={`w-full text-left px-4 py-3 border-b border-border/60 transition-colors ${
                                        isActive
                                            ? "bg-primary/10 border-l-2 border-l-primary"
                                            : "hover:bg-muted/60"
                                    }`}
                                    data-testid={`rav-lesson-${i}`}
                                >
                                    <div className="text-[11px] text-muted-foreground tracking-wider uppercase">
                                        Lesson {i + 1}
                                    </div>
                                    <div className={`text-sm font-medium truncate ${isActive ? "text-primary" : ""}`}>
                                        {l.title || "Untitled"}
                                    </div>
                                    <div className="mt-1 flex items-center gap-1 text-[11px]">
                                        {has ? (
                                            <span className="inline-flex items-center gap-1 text-emerald-700">
                                                <CheckCircle2 className="h-3 w-3" />
                                                ~{mins} min · {slideCount} slides
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">No storyboard</span>
                                        )}
                                        {stLabel === "generating" && (
                                            <span className="inline-flex items-center gap-1 text-primary ml-1">
                                                <Loader2 className="h-3 w-3 animate-spin" /> Generating
                                            </span>
                                        )}
                                        {stLabel === "rendering" && (
                                            <span className="inline-flex items-center gap-1 text-primary ml-1">
                                                <Loader2 className="h-3 w-3 animate-spin" /> Rendering
                                            </span>
                                        )}
                                        {stLabel === "error" && (
                                            <span className="inline-flex items-center gap-1 text-destructive ml-1">
                                                <AlertCircle className="h-3 w-3" />
                                                Error
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </aside>

                    {/* Editor */}
                    <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                        {/* Lesson header strip with prev/next */}
                        <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-card">
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setActive((i) => Math.max(0, i - 1))}
                                disabled={active === 0}
                                aria-label="Previous lesson"
                                data-testid="rav-prev"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                    Lesson {active + 1} of {lessons.length}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <div className="text-base font-display text-primary truncate min-w-0">
                                        {currentLesson.title}
                                    </div>
                                    {(currentBoard?.slides || []).length > 0 && (
                                        <span
                                            className="inline-flex items-center gap-1 shrink-0 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-foreground/80"
                                            data-testid="rav-duration-pill"
                                        >
                                            <Clock className="h-3 w-3" />
                                            ~{boardDurationMinutes(currentBoard)} min · {currentBoard.slides.length} slides
                                        </span>
                                    )}
                                </div>
                            </div>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setActive((i) => Math.min(lessons.length - 1, i + 1))}
                                disabled={active === lessons.length - 1}
                                aria-label="Next lesson"
                                data-testid="rav-next"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Action bar */}
                        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-border bg-muted/20">
                            {!currentBoard?.slides?.length ? (
                                <Button
                                    size="sm"
                                    onClick={() => generateOne(currentLesson)}
                                    disabled={isGenerating}
                                    className="gap-1.5"
                                    data-testid="rav-generate-one"
                                >
                                    {isGenerating ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Wand2 className="h-3.5 w-3.5" />
                                    )}
                                    {isGenerating ? "Drafting…" : "Generate storyboard"}
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => saveOne(currentLesson)}
                                        disabled={isSaving || !dirtyRef.current[currentLesson.id]}
                                        className="gap-1.5"
                                        data-testid="rav-save"
                                    >
                                        {isSaving ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Save className="h-3.5 w-3.5" />
                                        )}
                                        {dirtyRef.current[currentLesson.id] ? "Save edits" : "Saved"}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => generateOne(currentLesson)}
                                        disabled={isGenerating}
                                        className="gap-1.5"
                                        data-testid="rav-regenerate"
                                    >
                                        {isGenerating ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Sparkles className="h-3.5 w-3.5" />
                                        )}
                                        Regenerate
                                    </Button>
                                    <div className="flex-1" />
                                    <Button
                                        size="sm"
                                        onClick={() => renderOne(currentLesson)}
                                        disabled={isRendering}
                                        className="gap-1.5 bg-primary hover:bg-primary/90"
                                        data-testid="rav-render-one"
                                    >
                                        {isRendering ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Play className="h-3.5 w-3.5" />
                                        )}
                                        Render this lesson&apos;s video
                                    </Button>
                                </>
                            )}
                        </div>

                        {lessonError && (
                            <div className="px-5 py-2 text-[12px] text-destructive bg-destructive/5 border-b border-destructive/20 flex items-start gap-1.5">
                                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>{lessonError}</span>
                            </div>
                        )}

                        {/* Storyboard editor body */}
                        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                            {!currentBoard?.slides?.length ? (
                                <div className="flex flex-col items-center justify-center text-center py-16 gap-3 text-muted-foreground">
                                    <Layers className="h-10 w-10 text-muted-foreground/50" />
                                    <div>
                                        <div className="font-medium text-foreground">
                                            No storyboard yet
                                        </div>
                                        <div className="text-sm mt-1">
                                            Generate one with AI to start editing slides.
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                                            Video title
                                        </Label>
                                        <Input
                                            value={currentBoard.title || ""}
                                            onChange={(e) => updateBoard(
                                                currentLesson.id,
                                                (cur) => ({ ...cur, title: e.target.value }),
                                            )}
                                            data-testid="rav-board-title"
                                        />
                                    </div>
                                    {currentBoard.slides.map((slide, sIdx) => (
                                        <div
                                            key={sIdx}
                                            className="border border-border rounded-sm bg-card p-4 space-y-3"
                                            data-testid={`rav-slide-${sIdx}`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                                    Slide {sIdx + 1}
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-muted-foreground">Heading</Label>
                                                <Input
                                                    value={slide.heading}
                                                    onChange={(e) => updateBoard(
                                                        currentLesson.id,
                                                        (cur) => {
                                                            const slides = [...cur.slides];
                                                            slides[sIdx] = { ...slides[sIdx], heading: e.target.value };
                                                            return { ...cur, slides };
                                                        },
                                                    )}
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-xs text-muted-foreground">
                                                    Bullets (one per line — &lt; 70 chars each)
                                                </Label>
                                                <Textarea
                                                    rows={Math.max(3, (slide.bullets || []).length)}
                                                    value={(slide.bullets || []).join("\n")}
                                                    onChange={(e) => updateBoard(
                                                        currentLesson.id,
                                                        (cur) => {
                                                            const slides = [...cur.slides];
                                                            const bullets = e.target.value.split("\n")
                                                                .map((s) => s.replace(/^[-•*]\s+/, "").trim())
                                                                .filter(Boolean);
                                                            slides[sIdx] = { ...slides[sIdx], bullets };
                                                            return { ...cur, slides };
                                                        },
                                                    )}
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-xs text-muted-foreground">
                                                    Narration (spoken aloud, 40-90 words)
                                                </Label>
                                                <Textarea
                                                    rows={4}
                                                    value={slide.narration}
                                                    onChange={(e) => updateBoard(
                                                        currentLesson.id,
                                                        (cur) => {
                                                            const slides = [...cur.slides];
                                                            slides[sIdx] = { ...slides[sIdx], narration: e.target.value };
                                                            return { ...cur, slides };
                                                        },
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
